import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, IconButton, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, CircularProgress } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import Checkbox from '@mui/material/Checkbox';
import { getAllObjectTags, updateTagGlobally, bulkUpdateObjectTags, deleteTagGlobally, createTag } from '../../store/Objects';
import constants from '../../constants';

const TagsManager = ({
  objectType,
  onTagSelect,
  selectedTags = [],
  selectedObjects = [],
  onTagsApplied,
  onTagsChange, // For filter mode
  mode = 'manage', // 'manage', 'select', or 'filter'
}) => {

  const dispatch = useDispatch();

  // Get tags from Redux store based on object type
  const schema = constants.getObjectSchema(objectType);
  const stateKey = schema?.type || objectType; // Use objectType directly if no schema found (for agents)
  const { deals, contacts, companies, agents, workflows } = useSelector(state => state['Objects']);
  const objectState = { deals, contacts, companies, agents, workflows }[stateKey];
  const allTags = objectState?.allTags || [];

  // State management
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [editingTagName, setEditingTagName] = useState('');
  const [selectedColor, setSelectedColor] = useState('#EF4444');
  const [editingTagColor, setEditingTagColor] = useState('#EF4444');
  const [searchText, setSearchText] = useState('');
  const [tagsToApply, setTagsToApply] = useState([]);
  const [isApplying, setIsApplying] = useState(false);
  // Convert array of tag names to object format for filter mode
  const [filterTags, setFilterTags] = useState(() => {
    if (mode === 'filter' && Array.isArray(selectedTags)) {
      return selectedTags.reduce((acc, tag) => ({ ...acc, [tag]: true }), {});
    }
    return {};
  });

  // Color palette
  const colorPalette = [
    '#EC4899', '#F472B6', '#F87171', '#FB923C', '#FBBF24',
    '#FDE047', '#A3E635', '#FF0000', '#6EE7B7', '#5EEAD4',
    '#22D3EE', '#60A5FA', '#818CF8', '#A78BFA', '#C084FC',
    '#E879F9', '#FB7185', '#FCA5A5', '#FDBA74', '#FCD34D',
    '#BEF264', '#86EFAC', '#67E8F9', '#93C5FD', '#C4B5FD'
  ];

  // Helper functions for backward compatibility with string and object tags
  const getTagName = (tag) => {
    return typeof tag === 'string' ? tag : tag?.name || '';
  };

  const getTagColor = (tag) => {
    return typeof tag === 'string' ? '#6B7280' : tag?.color || '#6B7280';
  };

  const createTagObject = (tag, defaultColor = '#6B7280') => {
    if (typeof tag === 'string') {
      return { name: tag, color: defaultColor };
    }
    return tag;
  };

  // Load tags on mount
  useEffect(() => {
    if (objectType) dispatch(getAllObjectTags({ objectType }));
  }, [objectType, dispatch]);

  // Update filter tags when selectedTags prop changes (for filter mode)
  useEffect(() => {
    if (mode === 'filter' && Array.isArray(selectedTags)) {
      setFilterTags(selectedTags.reduce((acc, tag) => ({ ...acc, [getTagName(tag)]: true }), {}));
    }
  }, [selectedTags, mode]);

  // Handle menu open
  const handleMenuClick = (event, tag) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedTag(tag);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTag(null);
  };

  // Handle create tag
  const handleCreateTag = () => {
    setNewTagName('');
    setSelectedColor('#EF4444');
    setCreateModalOpen(true);
    handleMenuClose();
  };

  // Handle save new tag
  const handleSaveNewTag = () => {
    if (newTagName.trim()) {
      const newTag = { name: newTagName.trim(), color: selectedColor };
      // In filter mode, create tag without assigning to objects
      if (mode === 'filter') {
        setIsApplying(true);
        dispatch(createTag({
          objectType, tagName: newTag.name, tagColor: newTag.color,
          successCallback: () => {
            setIsApplying(false);
            setCreateModalOpen(false);
            setNewTagName('');
            setSelectedColor('#EF4444');
            dispatch(getAllObjectTags({ objectType }));
            if (onTagsApplied) onTagsApplied(); // Refresh the parent list
          },
          failCallback: () => {
            setIsApplying(false);
          }
        }));
      } else {
        // In select mode, just add to local state (will be created when applied)
        setCreateModalOpen(false);
        setNewTagName('');
        setSelectedColor('#EF4444');
        if (mode === 'select') setTagsToApply([...tagsToApply, newTag]);
        else if (onTagSelect) onTagSelect(newTag);
        setTimeout(() => { dispatch(getAllObjectTags({ objectType })); }, 500);
      }
    }
  };

  // Handle edit tag
  const handleEditTag = () => {
    const tagName = getTagName(selectedTag);
    const tagColor = getTagColor(selectedTag);

    setEditingTagName(tagName);
    setEditingTagColor(tagColor);
    setEditModalOpen(true);
    setAnchorEl(null); // Close menu without clearing selectedTag
  };

  // Handle update tag
  const handleUpdateTag = () => {
    if (editingTagName.trim() && selectedTag) {
      const oldTagName = getTagName(selectedTag);
      setIsApplying(true);
      dispatch(updateTagGlobally({
        objectType, oldTagName, newTagName: editingTagName.trim(), newTagColor: editingTagColor,
        successCallback: () => {
          setIsApplying(false);
          setEditModalOpen(false);
          setEditingTagName('');
          setEditingTagColor('#EF4444');
          setSelectedTag(null); // Clear selected tag after update
          dispatch(getAllObjectTags({ objectType }));
          if (onTagsApplied) onTagsApplied(); // Refresh the parent list
        },
        failCallback: () => {
          setIsApplying(false);
        }
      }));
    }
  };

  // Handle delete tag
  const handleDeleteTag = () => {
    setDeleteConfirmOpen(true);
    setAnchorEl(null); // Close menu without clearing selectedTag
  };

  // Handle confirm delete
  const handleConfirmDelete = () => {
    if (selectedTag) {
      const tagName = getTagName(selectedTag);

      // Check if we're in selection mode with selected objects
      if (mode === 'select' && selectedObjects && selectedObjects.length > 0) {
        setIsApplying(true);
        // Remove tag from specific selected objects only
        dispatch(bulkUpdateObjectTags({
          objectType,
          objectIds: selectedObjects,
          tagsToAdd: [],
          tagsToRemove: [{ name: tagName, color: getTagColor(selectedTag) }],
          successCallback: () => {
            setIsApplying(false);
            setDeleteConfirmOpen(false);
            setSelectedTag(null);
            dispatch(getAllObjectTags({ objectType }));
            if (onTagsApplied) onTagsApplied();
          },
          failCallback: () => {
            setIsApplying(false);
          }
        }));
      } else if (mode === 'filter') {
        setIsApplying(true);
        // In filter mode (All Tags), use global delete
        dispatch(deleteTagGlobally({
          objectType,
          tagName,
          successCallback: () => {
            setIsApplying(false);
            setDeleteConfirmOpen(false);
            setSelectedTag(null);
            dispatch(getAllObjectTags({ objectType }));
            if (onTagsApplied) onTagsApplied(); // Refresh the parent list
          },
          failCallback: () => {
            setIsApplying(false);
          }
        }));
      } else {
        // For manage mode without selected objects
        setDeleteConfirmOpen(false);
      }
    }
  };

  // Handle tag selection in selection mode
  const handleTagSelection = (tag) => {
    if (mode !== 'select') return;
    const tagName = getTagName(tag);
    const isSelected = tagsToApply.some(t => getTagName(t) === tagName);
    if (isSelected) setTagsToApply(tagsToApply.filter(t => getTagName(t) !== tagName));
    else setTagsToApply([...tagsToApply, tag]);

  };

  // Handle tag filter selection (for filter mode)
  const handleFilterTagToggle = (tag) => {
    const tagName = getTagName(tag);
    const newFilterTags = { ...filterTags };
    if (newFilterTags[tagName]) delete newFilterTags[tagName];
    else newFilterTags[tagName] = true;
    setFilterTags(newFilterTags);

    // Call onTagsChange with array of selected tag names
    if (onTagsChange) {
      const selectedTagNames = Object.keys(newFilterTags).filter(key => newFilterTags[key]);
      onTagsChange(selectedTagNames);
    }
  };

  // Handle applying selected tags to selected objects
  const handleApplySelectedTags = () => {
    if (mode === 'select' && selectedObjects.length > 0 && tagsToApply.length > 0) {
      setIsApplying(true);
      dispatch(bulkUpdateObjectTags({
        objectType, objectIds: selectedObjects, tagsToAdd: tagsToApply, tagsToRemove: [],
        successCallback: () => {
          // Merge existing tags with new tags to pass complete list
          const existingTagNames = selectedTags.map(tag => tag.name || tag);
          const newTagNames = tagsToApply.map(tag => tag.name || tag);
          const allTagNames = [...new Set([...existingTagNames, ...newTagNames])];

          // Create complete tag objects array
          const mergedTags = allTagNames.map(name => {
            const existingTag = selectedTags.find(t => (t.name || t) === name);
            const newTag = tagsToApply.find(t => (t.name || t) === name);
            return existingTag || newTag || { name, color: '#6B7280' };
          });

          setIsApplying(false);
          // Pass the complete merged tags back to parent
          if (onTagsApplied) onTagsApplied(mergedTags);
        },
        failCallback: () => {
          setIsApplying(false);
        }
      }));
    }
  };

  // Filter tags based on search
  const filteredTags = allTags.filter(tag => {
    const tagName = typeof tag === 'string' ? tag : tag?.name || '';
    return tagName.toLowerCase().includes(searchText.toLowerCase());
  });

  return (
    <div className='tags-dropdown' style={{ padding: '6px 17px 17px', }}>
      {createModalOpen ? (
        <>
          <div className='heading-area'>
            <h5>
              <button className='icon' onClick={() => setCreateModalOpen(false)}>
                <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 13L1 7L7 1" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>
              </button>
              Create New Tag
            </h5>
          </div>
          <div className='group-form'>
            <label>Tag Name</label>
            <TextField
              fullWidth
              placeholder="Add tag name"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
          <h5>Choose a Color</h5>
          <div className='color-grid'>
            {colorPalette.map((color) => (
              <div className='color-box'
                key={color}
                onClick={() => setSelectedColor(color)}
                style={{
                  backgroundColor: color,
                  border: selectedColor === color ? '2px solid var(--primary-color)' : 'none',
                  '&:hover': {
                    transform: 'scale(1.1)'
                  }
                }}
              ></div>
            ))}
          </div>
          <button
            className='btn-style-new mt-3'
            fullWidth
            onClick={handleSaveNewTag}
            disabled={!newTagName.trim() || isApplying}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {isApplying ? (
              <>
                <CircularProgress size={16} style={{ color: 'white' }} />
                Creating...
              </>
            ) : (
              'Create'
            )}
          </button>
        </>
      ) : editModalOpen ? (
        <>
          <div className='heading-area'>
            <h5>
              <button className='icon' onClick={() => setEditModalOpen(false)}>
                <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 13L1 7L7 1" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>
              </button>
              Edit Tag
              {/* <IconButton  size="small">
            <CloseIcon fontSize="small" />
          </IconButton> */}
            </h5>
          </div>
          <div className='group-form'>
            <label>Tag Name</label>
            <TextField
              fullWidth
              placeholder="Add tag name"
              value={editingTagName}
              onChange={(e) => setEditingTagName(e.target.value)}
              sx={{ marginTop: 2 }}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
          <h5>Choose a Color</h5>
          <div className='color-grid'>
            {colorPalette.map((color) => (
              <div className='color-box'
                key={color}
                onClick={() => setEditingTagColor(color)}
                style={{
                  backgroundColor: color,
                  border: editingTagColor === color ? '2px solid var(--primary-color)' : 'none',
                  '&:hover': {
                    transform: 'scale(1.1)'
                  }
                }}
              ></div>
            ))}
          </div>
          <button
            className='btn-style-new mt-3'
            fullWidth
            onClick={() => {
              console.log('Button clicked directly!');
              handleUpdateTag();
            }}
            disabled={isApplying}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {isApplying ? (
              <>
                <CircularProgress size={16} style={{ color: 'white' }} />
                Updating...
              </>
            ) : (
              'Update'
            )}
          </button>
        </>
      ) : (<>
        {/* Header - Hide for filter mode */}
        {mode !== 'filter' && (
          <div className='heading-area'>
            <h5>{mode === 'select' ? `Add Tags to ${selectedObjects.length} ${objectType}` : 'Manage Tags'}</h5>
            {mode === 'select' && tagsToApply.length > 0 && (
              <h5>
                {tagsToApply.length} tag{tagsToApply.length !== 1 ? 's' : ''} selected
              </h5>
            )}
          </div>
        )}
        <div className='group-form'>
          {/* Search Bar */}
          <TextField
            fullWidth
            placeholder="Search tags..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ marginBottom: mode === 'filter' ? 2 : 3 }}
            size="small"
            onKeyDown={(e) => e.stopPropagation()}
          />
        </div>
        {/* Selected Tags Section - Hide for filter mode */}
        {mode === 'select' && tagsToApply.length > 0 && (
          <Box sx={{
            padding: '5px 0 0',
            borderTop: '1px solid #e5e7eb',
            marginTop: 2,
            width: '250px'
          }}>
            <Typography variant="caption" sx={{
              color: '#6b7280',
              marginBottom: '8px',
              display: 'block',
              fontSize: '12px'
            }}>
              Selected tags to add:
            </Typography>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px',
              marginBottom: '16px'
            }}>
              {tagsToApply.map((tag, index) => {
                const tagName = typeof tag === 'string' ? tag : tag.name;
                const tagColor = typeof tag === 'string' ? '#10b981' : tag.color;
                return (
                  <div className='tag-bottom-box' key={index} style={{ backgroundColor: tagColor, }}>
                    {tagName}
                    <button className='btn-tag-cancel' onClick={() => setTagsToApply(tagsToApply.filter((_, i) => i !== index))}>
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
            <button
              className='btn-style-new mb-3'
              style={{ width: '100%', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              onClick={handleApplySelectedTags}
              disabled={isApplying}
            >
              {isApplying ? (
                <>
                  <CircularProgress size={16} style={{ color: 'white' }} />
                  Applying tags...
                </>
              ) : (
                `Apply ${tagsToApply.length} Tag${tagsToApply.length !== 1 ? 's' : ''}`
              )}
            </button>
          </Box>
        )}
        {/* Tags List */}
        <div className='tag-list-area' sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}>
          {filteredTags.map((tag) => {
            const tagName = getTagName(tag);
            const tagColor = getTagColor(tag);
            // Check if tag is already applied to the object OR being selected in this session
            const isAlreadyApplied = selectedTags.some(t => getTagName(t) === tagName);
            const isSelected = mode === 'select'
              ? tagsToApply.some(t => getTagName(t) === tagName) || isAlreadyApplied
              : mode === 'filter'
                ? filterTags[tagName] || false
                : selectedTags.some(t => getTagName(t) === tagName);

            return (
              <div
                className='taglist-box'
                key={tagName}
                style={{
                  backgroundColor: tagColor,
                  cursor: mode === 'select' || mode === 'filter' ? 'pointer' : 'default',
                  opacity: isSelected ? 0.7 : 1,
                  border: isSelected ? '1px solid #ffffff' : 'none',
                  width: '100%',
                  '&:hover': {
                    opacity: mode === 'select' || mode === 'filter' ? 0.8 : 1,
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  }
                }}
                onClick={() => {
                  if (mode === 'select') handleTagSelection(tag);
                  else if (mode === 'filter') handleFilterTagToggle(tag);
                  else if (onTagSelect) onTagSelect(tag);
                }}
              >
                <div className='text-box'>
                  {mode === 'filter' && (
                    <Checkbox
                      checked={isSelected}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFilterTagToggle(tag);
                      }}
                      size="small"
                      sx={{ padding: 0, marginRight: 1, color: 'white', '&.Mui-checked': { color: 'white' } }}
                    />
                  )}
                  <Typography sx={{
                    fontWeight: 500,
                    fontSize: '14px',
                    textTransform: mode === 'filter' ? 'none' : 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {tagName}
                    {mode !== 'filter' && isSelected && (
                      <CheckIcon sx={{ marginLeft: 1, fontSize: 16 }} />
                    )}
                  </Typography>
                </div>

                <IconButton
                  size="small"
                  onClick={(e) => handleMenuClick(e, tag)}
                  sx={{
                    color: 'white',
                    padding: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)'
                    }
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </div>
            );
          })}
        </div>
        {/* CREATE NEW TAG Button at bottom of list */}
        <button className='btn-style-border' style={{ width: '100%', marginTop: '10px', fontSize: '12px' }} onClick={handleCreateTag}>
          Create New Tag
        </button>

        {/* Empty State */}
        {filteredTags.length === 0 && (
          <div className='no-tags-found'>
            <h5>No tags found</h5>
            <p>{searchText ? 'Try adjusting your search' : 'Create your first tag to get started'}</p>
          </div>
        )}


        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          slotProps={{ paper: { sx: { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' } } }}
        >
          <MenuItem onClick={handleEditTag}>
            <EditIcon sx={{ marginRight: 1, fontSize: 18 }} />
            Edit
          </MenuItem>
          <MenuItem onClick={handleDeleteTag} sx={{ color: '#DC2626' }}>
            <DeleteIcon sx={{ marginRight: 1, fontSize: 18 }} />
            {mode === 'select' && selectedObjects?.length > 0
              ? `Remove from ${selectedObjects.length} Selected`
              : 'Delete'}
          </MenuItem>
        </Menu>
      </>
      )}

      {/* Create Tag Modal */}
      {/* <Dialog
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: { borderRadius: '12px', padding: '8px' }
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <Typography variant="h6">Create New Tag</Typography>

        </DialogTitle>
        <DialogContent sx={{ paddingTop: 3 }}>

        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>

        </DialogActions>
      </Dialog> */}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        sx={{ padding: '25px' }}
      >
        <DialogTitle sx={{ fontSize: '15px', fontWeight: '600' }}>
          {mode === 'select' && selectedObjects?.length > 0
            ? 'Remove Tag from Selected Items'
            : mode === 'filter'
              ? 'Delete Tag Globally'
              : 'Delete Tag'}
        </DialogTitle>
        <DialogContent sx={{ padding: '25px' }}>
          <Typography sx={{ fontSize: '13px' }}>
            Are you sure you want to
            {mode === 'filter'
              ? ` delete the tag "${getTagName(selectedTag)}" from all ${objectType}?`
              : mode === 'select' && selectedObjects?.length > 0
                ? ` remove the tag "${getTagName(selectedTag)}" from ${selectedObjects.length} selected ${objectType}?`
                : ` remove the tag "${getTagName(selectedTag)}"?`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '15px 20px' }}>
          <button className='btn-style-border' onClick={() => setDeleteConfirmOpen(false)} disabled={isApplying}>Cancel</button>
          <button
            className='btn-style-new'
            onClick={handleConfirmDelete}
            disabled={isApplying}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
          >
            {isApplying ? (
              <>
                <CircularProgress size={16} style={{ color: 'white' }} />
                {mode === 'select' && selectedObjects?.length > 0 ? 'Removing...' : 'Deleting...'}
              </>
            ) : (
              mode === 'select' && selectedObjects?.length > 0 ? 'Remove' : 'Delete'
            )}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TagsManager;